import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { X, Plus } from 'lucide-react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { apiClient } from '../../lib/api';
import { useIsMobile } from '../../hooks/useMediaQuery';
import { formatVariableName } from '../../utils/nameUtils';

// Default email variables as fallback when columns are empty
const DEFAULT_EMAIL_VARIABLES = [
  { key: 'first_name', label: 'First Name', description: "Recipient's first name" },
  { key: 'last_name', label: 'Last Name', description: "Recipient's last name" },
  { key: 'full_name', label: 'Full Name', description: "Recipient's full name" },
  { key: 'company_name', label: 'Company', description: "Recipient's company" },
  { key: 'job_title', label: 'Job Title', description: "Recipient's job title" },
  { key: 'email', label: 'Email', description: "Recipient's email address" },
];

interface EmailTemplate {
  id: string;
  name: string;
  subject: string;
  body: string;
  body_plain?: string;
  created_at?: string;
  updated_at?: string;
}

interface TemplateEditorModalProps {
  isOpen: boolean;
  onClose: () => void;
  templates: EmailTemplate[];
  initialTemplate?: EmailTemplate | null;
  columns: string[];
  onSave: (template: EmailTemplate) => void;
}

export const TemplateEditorModal: React.FC<TemplateEditorModalProps> = ({
  isOpen,
  onClose,
  templates,
  initialTemplate,
  columns,
  onSave,
}) => {
  const quillRef = useRef<ReactQuill>(null);
  const isMobile = useIsMobile();
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(
    initialTemplate?.id || null
  );
  const [editSubject, setEditSubject] = useState(initialTemplate?.subject || '');
  const [editBody, setEditBody] = useState(initialTemplate?.body || '');
  const [templateName, setTemplateName] = useState(initialTemplate?.name || '');
  const [isSaving, setIsSaving] = useState(false);
  const [showSlashMenu, setShowSlashMenu] = useState(false);
  const [slashMenuPosition, setSlashMenuPosition] = useState({ top: 0, left: 0 });
  const [slashFilter, setSlashFilter] = useState('');

  // Build available variables from columns, with fallback to defaults
  const availableVariables = useMemo(() => {
    if (!columns || columns.length === 0) {
      return DEFAULT_EMAIL_VARIABLES;
    }
    return columns.map(col => ({
      key: col.toLowerCase().replace(/\s+/g, '_'),
      label: formatVariableName(col),
      description: `Insert ${col}`,
    }));
  }, [columns]);

  const filteredVariables = useMemo(() => {
    if (!slashFilter) return availableVariables;
    return availableVariables.filter(v =>
      v.label.toLowerCase().includes(slashFilter.toLowerCase()) ||
      v.key.toLowerCase().includes(slashFilter.toLowerCase())
    );
  }, [availableVariables, slashFilter]);

  // Load template when selected
  useEffect(() => {
    if (selectedTemplateId === 'new') {
      setEditSubject('');
      setEditBody('');
      setTemplateName('');
    } else if (selectedTemplateId) {
      const template = templates.find(t => t.id === selectedTemplateId);
      if (template) {
        setEditSubject(template.subject);
        setEditBody(template.body);
        setTemplateName(template.name);
      }
    }
  }, [selectedTemplateId, templates]);

  // Set initial template on open
  useEffect(() => {
    if (isOpen && initialTemplate) {
      setSelectedTemplateId(initialTemplate.id);
      setEditSubject(initialTemplate.subject);
      setEditBody(initialTemplate.body);
      setTemplateName(initialTemplate.name);
    } else if (isOpen && !initialTemplate) {
      // Default to first template or new
      if (templates.length > 0) {
        const first = templates[0];
        setSelectedTemplateId(first.id);
        setEditSubject(first.subject);
        setEditBody(first.body);
        setTemplateName(first.name);
      } else {
        setSelectedTemplateId('new');
      }
    }
  }, [isOpen, initialTemplate, templates]);

  const handleBodyChange = useCallback((content: string, _delta: any, source: string) => {
    setEditBody(content);

    // Only process user-initiated changes
    if (source !== 'user') return;

    // Check for slash command
    const quill = quillRef.current?.getEditor();
    if (!quill) return;

    const selection = quill.getSelection();
    if (!selection) return;

    const text = quill.getText(0, selection.index);
    const lastSlashIndex = text.lastIndexOf('/');

    if (lastSlashIndex !== -1) {
      const textAfterSlash = text.slice(lastSlashIndex + 1);
      // Only show menu if slash is recent (within 20 chars) and no space after it
      if (textAfterSlash.length < 20 && !textAfterSlash.includes(' ') && !textAfterSlash.includes('\n')) {
        setSlashFilter(textAfterSlash);

        // Get cursor position relative to the editor (for absolute positioning)
        const bounds = quill.getBounds(selection.index);
        if (bounds) {
          // Position relative to the editor container (absolute positioning)
          setSlashMenuPosition({
            top: bounds.top + bounds.height + 4,
            left: bounds.left,
          });
          setShowSlashMenu(true);
        }
      } else {
        setShowSlashMenu(false);
      }
    } else {
      setShowSlashMenu(false);
    }
  }, []);

  const insertVariable = useCallback((variableKey: string) => {
    const quill = quillRef.current?.getEditor();
    if (!quill) return;

    const selection = quill.getSelection();
    if (!selection) return;

    // Find and remove the slash and any filter text
    const text = quill.getText(0, selection.index);
    const lastSlashIndex = text.lastIndexOf('/');

    if (lastSlashIndex !== -1) {
      const deleteLength = selection.index - lastSlashIndex;
      quill.deleteText(lastSlashIndex, deleteLength);
      quill.insertText(lastSlashIndex, `{{${variableKey}}}`);
      quill.setSelection(lastSlashIndex + variableKey.length + 4, 0);
    }

    setShowSlashMenu(false);
    setSlashFilter('');
  }, []);

  const handleSave = async () => {
    if (!editSubject.trim()) return;

    setIsSaving(true);
    try {
      if (selectedTemplateId === 'new') {
        // Create new template
        const name = templateName.trim() || editSubject.substring(0, 50);
        const template = await apiClient.createEmailTemplate({
          name,
          subject: editSubject,
          body: editBody,
        });
        onSave(template);
      } else if (selectedTemplateId) {
        // Update existing template
        const template = await apiClient.updateEmailTemplate(selectedTemplateId, {
          name: templateName.trim() || editSubject.substring(0, 50),
          subject: editSubject,
          body: editBody,
        });
        onSave(template);
      }
    } catch (err) {
      console.error('Failed to save template:', err);
    } finally {
      setIsSaving(false);
    }
  };

  const handleSelectAndUse = async () => {
    // Find the current template and pass it back
    const template = templates.find(t => t.id === selectedTemplateId);
    if (template) {
      onSave({
        ...template,
        subject: editSubject,
        body: editBody,
      });
      onClose();
    } else if (selectedTemplateId === 'new') {
      // For new templates, save to backend first then use
      setIsSaving(true);
      try {
        const name = templateName.trim() || editSubject.substring(0, 50);
        const template = await apiClient.createEmailTemplate({
          name,
          subject: editSubject,
          body: editBody,
        });
        onSave(template);
      } catch (err) {
        console.error('Failed to save template:', err);
        // Still close the modal but with a temp template as fallback
        onSave({
          id: 'temp-' + Date.now(),
          name: templateName || 'Untitled',
          subject: editSubject,
          body: editBody,
        });
      } finally {
        setIsSaving(false);
        onClose();
      }
    }
  };

  const quillModules = useMemo(() => ({
    toolbar: [
      ['bold', 'italic', 'underline'],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      ['link'],
      ['clean']
    ],
  }), []);

  const quillFormats = useMemo(() => [
    'bold', 'italic', 'underline', 'list', 'bullet', 'link'
  ], []);

  if (!isOpen) return null;

  // Shared content for the editor form
  const renderEditorContent = () => (
    <>
      {/* Template Name (for new templates) */}
      {selectedTemplateId === 'new' && (
        <div className={isMobile ? 'mb-3' : 'mb-4'}>
          <label className={`block font-medium text-gray-500 ${isMobile ? 'text-[12px] mb-1' : 'text-[11px] mb-1.5'}`}>Template Name</label>
          <input
            type="text"
            value={templateName}
            onChange={(e) => setTemplateName(e.target.value)}
            placeholder="My Email Template"
            className={`w-full bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900/10 focus:border-gray-300 ${isMobile ? 'px-3 py-2.5 text-[14px]' : 'px-3 py-2 text-[13px]'}`}
          />
        </div>
      )}

      <div className="border border-gray-200 rounded-xl overflow-hidden">
        {/* Subject Line */}
        <div className={`border-b border-gray-100 ${isMobile ? 'px-4 py-2.5' : 'px-5 py-3'}`}>
          <label className={`block font-medium text-gray-400 ${isMobile ? 'text-[11px] mb-0.5' : 'text-[10px] mb-1'}`}>Subject</label>
          <input
            type="text"
            value={editSubject}
            onChange={(e) => setEditSubject(e.target.value)}
            placeholder="Subject line..."
            className={`w-full font-medium text-gray-900 bg-transparent focus:outline-none placeholder:text-gray-400 ${isMobile ? 'text-[15px]' : 'text-[14px]'}`}
          />
        </div>

        {/* Email Body */}
        <div className="relative">
          <style>{`
            .template-editor .ql-toolbar {
              border: none;
              border-bottom: 1px solid #f3f4f6;
              background: transparent;
              padding: ${isMobile ? '6px 10px' : '8px 12px'};
              display: flex;
              align-items: center;
              gap: 2px;
            }
            .template-editor .ql-toolbar .ql-formats {
              display: flex;
              align-items: center;
              gap: 1px;
              margin-right: 8px;
              padding-right: 8px;
              border-right: 1px solid #e5e7eb;
            }
            .template-editor .ql-toolbar .ql-formats:last-child {
              margin-right: 0;
              padding-right: 0;
              border-right: none;
            }
            .template-editor .ql-toolbar button {
              width: ${isMobile ? '32px' : '28px'};
              height: ${isMobile ? '32px' : '28px'};
              padding: 4px;
              border-radius: 6px;
              display: flex;
              align-items: center;
              justify-content: center;
            }
            .template-editor .ql-toolbar button svg {
              width: 14px;
              height: 14px;
            }
            .template-editor .ql-toolbar button:hover {
              background: #f3f4f6;
            }
            .template-editor .ql-toolbar button.ql-active {
              background: #e5e7eb;
              color: #111827;
            }
            .template-editor .ql-container {
              border: none;
              font-family: inherit;
              font-size: ${isMobile ? '14px' : '13px'};
            }
            .template-editor .ql-editor {
              min-height: ${isMobile ? '180px' : '200px'};
              max-height: ${isMobile ? '280px' : '240px'};
              padding: ${isMobile ? '12px 14px' : '14px 18px'};
              line-height: 1.65;
              color: #374151;
            }
            .template-editor .ql-editor p {
              margin-bottom: 8px;
            }
            .template-editor .ql-editor ul, .template-editor .ql-editor ol {
              padding-left: 18px;
              margin-bottom: 8px;
            }
            .template-editor .ql-editor li {
              margin-bottom: 3px;
            }
            .template-editor .ql-editor.ql-blank::before {
              font-style: normal;
              color: #9ca3af;
              font-size: ${isMobile ? '14px' : '13px'};
            }
          `}</style>
          <div className="template-editor relative" style={{ overflow: 'visible' }}>
            <ReactQuill
              ref={quillRef}
              theme="snow"
              value={editBody}
              onChange={handleBodyChange}
              modules={quillModules}
              formats={quillFormats}
              placeholder="Write your email... Type / to insert variables"
            />
            {/* Slash Command Menu - uses absolute positioning relative to .template-editor */}
            {showSlashMenu && filteredVariables.length > 0 && (
              <div
                className="fixed bg-white rounded-xl shadow-2xl border border-gray-200 py-1.5 min-w-[200px] max-h-[240px] overflow-y-auto"
                style={{ top: slashMenuPosition.top, left: slashMenuPosition.left, zIndex: 9999 }}
              >
                <div className="px-3 py-1.5 text-[10px] font-medium text-gray-400 uppercase tracking-wide">
                  Variables
                </div>
                {filteredVariables.map((variable) => (
                  <button
                    type="button"
                    key={variable.key}
                    onClick={() => insertVariable(variable.key)}
                    className="w-full px-3 py-2 text-left hover:bg-gray-50 flex items-center justify-between gap-3 group transition-colors"
                  >
                    <div>
                      <p className="text-[12px] font-medium text-gray-900">{variable.label}</p>
                      <p className="text-[10px] text-gray-400">{variable.description}</p>
                    </div>
                    <code className="text-[9px] bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded font-mono group-hover:bg-gray-900 group-hover:text-white transition-colors">
                      /
                    </code>
                  </button>
                ))}
                {/* Bottom padding for scroll */}
                <div className="h-20" />
              </div>
            )}
          </div>
        </div>
      </div>
      {!isMobile && (
        <p className="mt-3 text-[11px] text-gray-400 text-center">
          Type <kbd className="px-1 py-0.5 bg-gray-100 rounded text-gray-500 font-mono text-[10px]">/</kbd> to insert personalization variables
        </p>
      )}
    </>
  );

  // Mobile Layout
  if (isMobile) {
    return (
      <div className="fixed inset-0 z-[301] bg-white flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 flex-shrink-0">
          <h3 className="text-[16px] font-semibold text-gray-900">
            {selectedTemplateId === 'new' ? 'New Template' : 'Edit Template'}
          </h3>
          <button
            onClick={() => {
              setShowSlashMenu(false);
              onClose();
            }}
            className="p-2 -mr-2 text-gray-400 hover:text-gray-600 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Template Selection - Horizontal Scroll */}
        <div className="border-b border-gray-100 bg-gray-50/50 flex-shrink-0">
          <div className="px-4 pt-3 pb-2">
            <p className="text-[11px] font-medium text-gray-400 uppercase tracking-wide">Templates</p>
          </div>
          <div className="flex gap-2 px-4 pb-3 overflow-x-auto" style={{ scrollbarWidth: 'none' }}>
            <button
              onClick={() => setSelectedTemplateId('new')}
              className={`flex-shrink-0 flex items-center gap-1 px-3 py-2 rounded-lg text-[12px] font-medium transition-all ${
                selectedTemplateId === 'new'
                  ? 'bg-gray-900 text-white'
                  : 'bg-white border border-gray-200 text-gray-600'
              }`}
            >
              <Plus className="w-3 h-3" />
              New
            </button>
            {templates.map(template => (
              <button
                key={template.id}
                onClick={() => setSelectedTemplateId(template.id)}
                className={`flex-shrink-0 px-3 py-2 rounded-lg text-[12px] font-medium transition-all max-w-[140px] truncate ${
                  selectedTemplateId === template.id
                    ? 'bg-gray-900 text-white'
                    : 'bg-white border border-gray-200 text-gray-600'
                }`}
              >
                {template.name}
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {renderEditorContent()}
        </div>

        {/* Footer */}
        <div className="px-4 py-3 border-t border-gray-200 flex items-center gap-2 flex-shrink-0 bg-gray-50/50">
          {selectedTemplateId !== 'new' && (
            <button
              onClick={handleSave}
              disabled={isSaving || !editSubject.trim()}
              className="px-3 py-2.5 text-[13px] font-medium text-gray-600 bg-white border border-gray-200 rounded-lg disabled:opacity-50"
            >
              {isSaving ? 'Saving...' : 'Save'}
            </button>
          )}
          <button
            onClick={handleSelectAndUse}
            disabled={!editSubject.trim() || isSaving}
            className="flex-1 py-2.5 text-[14px] font-medium text-white bg-gray-900 rounded-lg disabled:opacity-50"
          >
            {selectedTemplateId === 'new' ? (isSaving ? 'Creating...' : 'Create & Use') : 'Use Template'}
          </button>
        </div>
      </div>
    );
  }

  // Desktop Layout
  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 z-[300] animate-fadeIn"
        onClick={() => {
          setShowSlashMenu(false);
          onClose();
        }}
      />

      {/* Modal */}
      <div className="fixed inset-0 z-[301] flex items-center justify-center p-4">
        <div
          className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl flex overflow-hidden animate-fadeInScale"
          style={{ height: '560px' }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Left Navigation - Templates */}
          <div className="w-48 bg-gray-50/80 border-r border-gray-200 flex-shrink-0 flex flex-col">
            <div className="h-[57px] flex items-center px-4 border-b border-gray-200">
              <span className="text-[13px] font-semibold text-gray-900">Templates</span>
            </div>
            <nav className="flex-1 p-2 space-y-0.5 overflow-y-auto">
              {templates.map(template => (
                <button
                  key={template.id}
                  onClick={() => setSelectedTemplateId(template.id)}
                  className={`w-full px-3 py-2.5 rounded-lg text-left transition-all duration-150 ${
                    selectedTemplateId === template.id
                      ? 'bg-white text-gray-900 shadow-sm'
                      : 'text-gray-600 hover:bg-white/60 hover:text-gray-900'
                  }`}
                >
                  <p className="text-[12px] font-medium truncate">{template.name}</p>
                  <p className={`text-[10px] truncate mt-0.5 ${selectedTemplateId === template.id ? 'text-gray-500' : 'text-gray-400'}`}>
                    {template.subject}
                  </p>
                </button>
              ))}

              {templates.length === 0 && (
                <div className="px-3 py-4 text-center">
                  <p className="text-[11px] text-gray-400">No templates yet</p>
                </div>
              )}
            </nav>

            <div className="h-[65px] flex items-center px-2 border-t border-gray-200">
              <button
                onClick={() => setSelectedTemplateId('new')}
                className={`w-full flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-[12px] font-medium transition-colors ${
                  selectedTemplateId === 'new'
                    ? 'bg-gray-900 text-white'
                    : 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-50'
                }`}
              >
                <Plus className="w-3.5 h-3.5" />
                New Template
              </button>
            </div>
          </div>

          {/* Right Content */}
          <div className="flex-1 flex flex-col min-w-0 bg-white">
            {/* Header */}
            <div className="h-[57px] flex items-center justify-between px-6 border-b border-gray-200 flex-shrink-0">
              <h3 className="text-[14px] font-semibold text-gray-900">
                {selectedTemplateId === 'new' ? 'New Template' : 'Edit Template'}
              </h3>
              <button
                onClick={() => {
                  setShowSlashMenu(false);
                  onClose();
                }}
                className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6">
              {renderEditorContent()}
            </div>

            {/* Footer Actions */}
            <div className="h-[65px] px-6 border-t border-gray-200 flex items-center justify-between flex-shrink-0">
              <div className="flex items-center gap-2">
                {selectedTemplateId !== 'new' && (
                  <button
                    onClick={handleSave}
                    disabled={isSaving || !editSubject.trim()}
                    className="px-3 py-1.5 text-[12px] font-medium text-gray-600 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
                  >
                    {isSaving ? 'Saving...' : 'Save Template'}
                  </button>
                )}
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={onClose}
                  className="px-4 py-2 text-[13px] font-medium text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSelectAndUse}
                  disabled={!editSubject.trim() || isSaving}
                  className="px-4 py-2 text-[13px] font-medium text-white bg-gray-900 rounded-lg hover:bg-gray-800 transition-colors disabled:opacity-50"
                >
                  {selectedTemplateId === 'new' ? (isSaving ? 'Creating...' : 'Create & Use') : 'Use Template'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
